import React, {useState} from "react";
import Section from "./layouts/Section";
import {WebLink, SiteWide} from "./customComponents";
import { useSelector, useDispatch } from 'react-redux';

import {authActions} from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect} from '../redux/selectors/index';
import Helper1, {notify, money, customNormalModalStyles} from '../utility/Helper1';
import {homepage} from '../utility/APIRequests';

import Countdown from 'react-countdown';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {staticCountries} from '../utility/countries';
import {Images} from "./customComponents";
import Slider from "react-slick";

// Lucide React Icons
import { 
    Eye, 
    EyeOff, 
    Wallet, 
    Shield, 
    CheckCircle, 
    AlertCircle, 
    History,
    CreditCard,
    User,
    Building2,
    Lock,
    Save
} from 'lucide-react';

import {mainStyle, mainColor, header, footer, otherColors} from '../utility/styles';


function Bank({history}){

    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);

    const [loading, setloading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [errors, setErrors] = useState({});

    const [bank, setBank] = useState(userData?.bank ?? '');
    const [accountName, setAccountName] = useState(userData?.accountName ?? '');
    const [accountNumber, setAccountNumber] = useState(userData?.accountNumber ?? '');
    const [iban, setIban] = useState(userData?.iban ?? '');

    // State triggers for in-place edit toggle & secure display
    const [isEditing, setIsEditing] = useState(false);
    const [showFullNumber, setShowFullNumber] = useState(false);

    const dispatch = useDispatch();

    // Helper functions for validation
    const validateForm = () => {
        const newErrors = {};
        
        if (!bank.trim()) {
            newErrors.bank = 'Bank name is required';
        }

        if (!accountName.trim()) {
            newErrors.accountName = 'Account holder name is required';
        }

        if (!accountNumber.trim()) {
            newErrors.accountNumber = 'Account number is required';
        }

        if (!withdrawalPin) {
            newErrors.withdrawalPin = 'Security PIN is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const doAddressChange = (e) => {
        e.preventDefault();

        // Validate form using new validation
        if (!validateForm()) {
            return;
        }

        setloading(true);
        setLoadingText('Updating bank details...');

        const updateObject = {iban, bank, accountName, accountNumber, withdrawalPin};

        dispatch(authActions.onLogin(`account/account_change/0`, updateObject, response => {
            notify(response.msg, response.code)
            setloading(false);
            if(response.code == 1){
                setWithdrawalPin('');
                setErrors({});
                setIsEditing(false); // Swap back to overview on success!
            }
        }));
    }


    const bankDetailsForm = () => {
        const inputStyle = {
            width: "100%", borderRadius: 12, padding: "12px 14px",
            border: "1px solid var(--border-color)",
            background: "var(--neutral-100)", color: "var(--text-main)",
            fontSize: 14, fontFamily: "'DM Sans',sans-serif",
            outline: "none", boxSizing: "border-box",
            marginTop: 6
        };

        if (!isEditing) {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    
                    {/* Bank Card / Overview Container */}
                    <div style={{
                        background: "var(--card-bg)", border: "1px solid var(--card-border)",
                        boxShadow: "var(--card-shadow)", borderRadius: 20, overflow: "hidden",
                        transition: "all 0.3s ease"
                    }}>
                        {/* Virtual Card representation */}
                        <div style={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            padding: 24, position: "relative", minHeight: 180,
                            display: "flex", flexDirection: "column", justifyContent: "space-between",
                            color: "#ffffff"
                        }}>
                            {/* Decorative radial circles */}
                            <div style={{ position: "absolute", top: 0, right: 0, opacity: 0.15, width: 200, height: 200, background: "radial-gradient(circle, #ffffff, transparent)", borderRadius: "50%", transform: "translate(40px, -40px)", pointerEvents: "none" }} />
                            <div style={{ position: "absolute", bottom: 0, left: 0, opacity: 0.1, width: 120, height: 120, background: "radial-gradient(circle, #ffffff, transparent)", borderRadius: "50%", transform: "translate(-30px, 30px)", pointerEvents: "none" }} />

                            {/* Top Row: Bank name & Edit Button */}
                            <div style={{ display: "flex", zIndex: 2, justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
                                    <Building2 size={18} />
                                    {userData.bank || "NO BANK PROFILE"}
                                </span>
                                
                                <button 
                                    onClick={() => {
                                        setBank(userData.bank ?? '');
                                        setAccountName(userData.accountName ?? '');
                                        setAccountNumber(userData.accountNumber ?? '');
                                        setIsEditing(true);
                                    }}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "6px 12px", borderRadius: 10,
                                        background: "rgba(255, 255, 255, 0.15)",
                                        border: "1px solid rgba(255, 255, 255, 0.25)",
                                        color: "#ffffff", fontSize: 11, fontWeight: 600,
                                        cursor: "pointer", transition: "all 0.2s"
                                    }}
                                >
                                    Edit
                                </button>
                            </div>

                            {/* Center Row: Account Number */}
                            <div style={{ margin: "28px 0", zIndex: 2 }}>
                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.7)", fontFamily: "'DM Sans', sans-serif" }}>Account Number</p>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                    <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.08em", fontFamily: "'Syne', sans-serif" }}>
                                        {userData.accountNumber ? (
                                            showFullNumber ? userData.accountNumber : (
                                                userData.accountNumber.substring(0,3) + ' •••• •••• ' + userData.accountNumber.slice(-3)
                                            )
                                        ) : "Not Configured"}
                                    </span>
                                    {userData.accountNumber && (
                                        <button 
                                            type="button"
                                            onClick={() => setShowFullNumber(!showFullNumber)}
                                            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer", display: "flex", alignItems: "center", padding: 0 }}
                                        >
                                            {showFullNumber ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Bottom Row: Account Holder Name */}
                            <div style={{ display: "flex", zIndex: 2, justifyContent: "space-between", alignItems: "flex-end" }}>
                                <div>
                                    <p style={{ margin: 0, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.7)", fontFamily: "'DM Sans', sans-serif" }}>Account Holder</p>
                                    <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>
                                        {userData.accountName || "NOT SET"}
                                    </p>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 8, background: "rgba(255, 255, 255, 0.12)", fontSize: 9, fontWeight: 600 }}>
                                    <Shield size={10} />
                                    SECURE
                                </div>
                            </div>
                        </div>

                        {/* Additional Details Section */}
                        <div style={{ padding: 16 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-color)" }}>
                                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Bank Name</span>
                                    <span style={{ color: "var(--text-main)", fontSize: 12, fontWeight: 600 }}>{userData.bank || "Not Configured"}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-color)" }}>
                                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Account Type</span>
                                    <span style={{ color: "var(--text-main)", fontSize: 12, fontWeight: 600 }}>Naira Savings Account</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Security Status</span>
                                    <span style={{ color: "#22c55e", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                                        <CheckCircle size={12} />
                                        Encrypted & Protected
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            );
        }

        // isEditing is true:
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                
                {/* Form Card */}
                <div style={{
                    background: "var(--card-bg)", border: "1px solid var(--card-border)",
                    boxShadow: "var(--card-shadow)", borderRadius: 16, padding: 20
                }}>
                    <h3 style={{ color: "var(--text-main)", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif", display: "flex", alignItems: "center", gap: 8, margin: "0 0 20px" }}>
                        <Building2 style={{ color: "#3b82f6" }} size={20} />
                        Update Banking Details
                    </h3>

                    <form onSubmit={doAddressChange} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {/* Bank Name */}
                        <div>
                            <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                Bank Name
                            </label>
                            <input
                                type="text"
                                value={bank}
                                onChange={(e) => setBank(e.target.value)}
                                style={{ ...inputStyle, border: errors.bank ? '1.5px solid var(--error-500)' : '1px solid var(--border-color)' }}
                                placeholder="Enter bank name (e.g. Kuda Bank)"
                            />
                            {errors.bank && (
                                <p style={{ color: "var(--error-500)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "4px 0 0" }}>{errors.bank}</p>
                            )}
                        </div>

                        {/* Account Holder Name */}
                        <div>
                            <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                Account Holder Name
                            </label>
                            <input
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                style={{ ...inputStyle, border: errors.accountName ? '1.5px solid var(--error-500)' : '1px solid var(--border-color)' }}
                                placeholder="Enter full name on account"
                            />
                            {errors.accountName && (
                                <p style={{ color: "var(--error-500)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "4px 0 0" }}>{errors.accountName}</p>
                            )}
                        </div>

                        {/* Account Number */}
                        <div>
                            <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                Account Number
                            </label>
                            <input
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value)}
                                style={{ ...inputStyle, border: errors.accountNumber ? '1.5px solid var(--error-500)' : '1px solid var(--border-color)' }}
                                placeholder="Enter 10-digit account number"
                            />
                            {errors.accountNumber && (
                                <p style={{ color: "var(--error-500)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "4px 0 0" }}>{errors.accountNumber}</p>
                            )}
                        </div>

                        {/* Security PIN */}
                        <div>
                            <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                Security PIN
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showPin ? 'text' : 'password'}
                                    value={withdrawalPin}
                                    onChange={(e) => setWithdrawalPin(e.target.value)}
                                    style={{ ...inputStyle, paddingRight: 40, border: errors.withdrawalPin ? '1.5px solid var(--error-500)' : '1px solid var(--border-color)' }}
                                    placeholder="Enter your 4-digit PIN"
                                    maxLength={4}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPin(!showPin)}
                                    style={{
                                        position: "absolute", right: 12, top: "50%", transform: "translateY(-40%)",
                                        background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)"
                                    }}
                                >
                                    {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {errors.withdrawalPin && (
                                <p style={{ color: "var(--error-500)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: "4px 0 0" }}>{errors.withdrawalPin}</p>
                            )}
                        </div>

                        {/* Form actions (Save / Cancel) */}
                        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setErrors({});
                                }}
                                style={{
                                    flex: 1, borderRadius: 14, padding: "13px 0",
                                    border: "1px solid var(--border-color)", background: "var(--neutral-100)",
                                    color: "var(--text-muted)", fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif",
                                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                                }}
                            >
                                Cancel
                            </button>
                            
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    flex: 1, borderRadius: 14, padding: "13px 0",
                                    border: "none", cursor: loading ? "not-allowed" : "pointer",
                                    background: loading ? "rgba(59,130,246,0.2)" : "linear-gradient(135deg,#3b82f6,#2563eb)",
                                    color: "#ffffff", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                                    transition: "transform 0.2s ease, opacity 0.2s ease"
                                }}
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Save
                                    </>
                                )}
                            </button>
                        </div>

                        <div style={{ textAlign: "center", marginTop: 4 }}>
                            <p style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                🔒 Secured banking database connection
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <SiteWide id="main" title={`Bank Details`}>
            <Section loading={loading} overlayText={loadingText} title={'Bank Account Management'}>
                {bankDetailsForm()}
            </Section>
        </SiteWide>
    );

}

export default Bank;